/**
 * Enhanced Image Cache Service
 * 
 * Bietet fortschrittliche Funktionen zum Caching von Bildpfaden,
 * Vorausladen von Bildern und Performance-Optimierung.
 */

// Cache-Keys
const CACHE_KEY_PREFIX = 'splinterlands_image_cache_';
const CACHE_TIMING_PREFIX = 'splinterlands_image_timing_';
const CACHE_PRELOAD_QUEUE = 'splinterlands_preload_queue';
const CACHE_FAILED_URLS = 'splinterlands_failed_urls';
const CACHE_EXPIRATION_DAYS = 3; // Cache bleibt 3 Tage gültig

// Memory Cache für schnelleren Zugriff
const memoryCache = new Map();
let preloadQueue = [];
let isPreloading = false;
const MAX_CONCURRENT_PRELOADS = 3;

/**
 * Prüft, ob ein Bild im Cache ist und die Cache-URL-Info noch gültig ist
 */
export const getImageFromCache = (card) => {
  try {
    // Eindeutiger Key für die Karte erstellen
    const cacheKey = getCacheKey(card);

    // Aus dem Memory-Cache holen (schnellster Zugriff)
    if (memoryCache.has(cacheKey)) {
      const cacheEntry = memoryCache.get(cacheKey);
      const now = new Date().getTime();
      const expirationTime = cacheEntry.timestamp + (CACHE_EXPIRATION_DAYS * 24 * 60 * 60 * 1000);
      
      if (now < expirationTime) {
        return cacheEntry.url;
      }
      
      // Memory-Cache ist abgelaufen, entfernen
      memoryCache.delete(cacheKey);
    }
    
    // Aus dem LocalStorage-Cache holen (persistierter Speicher)
    const cachedData = localStorage.getItem(cacheKey);
    if (!cachedData) return null;
    
    const { url, timestamp } = JSON.parse(cachedData);
    
    // Prüfen, ob der Cache noch gültig ist
    const now = new Date().getTime();
    const expirationTime = timestamp + (CACHE_EXPIRATION_DAYS * 24 * 60 * 60 * 1000);
    
    if (now > expirationTime) {
      // Cache ist abgelaufen, löschen
      localStorage.removeItem(cacheKey);
      return null;
    }
    
    // In Memory-Cache für schnelleren späteren Zugriff speichern
    memoryCache.set(cacheKey, { url, timestamp });
    
    return url;
  } catch (error) {
    console.warn("Error reading from image cache:", error);
    return null;
  }
};

/**
 * Speichert eine erfolgreiche Bild-URL im Cache
 */
export const saveImageToCache = (card, url) => {
  try {
    const cacheKey = getCacheKey(card);
    const timestamp = new Date().getTime();
    
    const data = { url, timestamp };
    
    // In Memory-Cache speichern für schnellen Zugriff
    memoryCache.set(cacheKey, data);
    
    // In LocalStorage für persistierten Speicher
    localStorage.setItem(cacheKey, JSON.stringify(data));
    
    // Entferne URL aus fehlgeschlagenen URLs
    removeFailedUrl(url);
    
    return true;
  } catch (error) {
    console.warn("Error saving to image cache:", error);
    return false;
  }
};

/**
 * Generiert einen eindeutigen Cache-Schlüssel für eine Karte
 */
const getCacheKey = (card) => {
  // Erstelle einen eindeutigen Schlüssel basierend auf Karteneigenschaften
  const signature = `${card.card_detail_id}_${card.gold ? 'gold' : 'normal'}_${card.foil || 0}_${card.edition || 0}`;
  return `${CACHE_KEY_PREFIX}${signature}`;
};

/**
 * Speichert Leistungsmetriken für erfolgreich geladene Bilder
 */
export const recordLoadTiming = (card, url, loadTimeMs) => {
  try {
    const key = `${CACHE_TIMING_PREFIX}${url}`;
    const timingData = {
      url,
      cardId: card.card_detail_id,
      loadTimeMs,
      timestamp: new Date().getTime()
    };
    
    localStorage.setItem(key, JSON.stringify(timingData));
    
    // Optimiere zukünftiges Preloading basierend auf Ladezeit
    if (loadTimeMs < 200) {
      // Schnell ladende Bilder haben höhere Preload-Priorität
      addToPreloadPriority(url);
    }
  } catch (error) {
    console.warn("Error recording load timing:", error);
  }
};

/**
 * Markiert eine URL als fehlgeschlagen, um wiederholte Fehler zu vermeiden
 */
export const markUrlAsFailed = (url) => {
  try {
    const failedUrls = JSON.parse(localStorage.getItem(CACHE_FAILED_URLS) || '{}');
    failedUrls[url] = Date.now();
    localStorage.setItem(CACHE_FAILED_URLS, JSON.stringify(failedUrls));
  } catch (error) {
    console.warn("Error marking URL as failed:", error);
  }
};

/**
 * Prüft, ob eine URL bereits als fehlgeschlagen markiert wurde
 */
export const hasUrlFailed = (url) => {
  try {
    const failedUrls = JSON.parse(localStorage.getItem(CACHE_FAILED_URLS) || '{}');
    const failedTimestamp = failedUrls[url];
    
    if (!failedTimestamp) return false;
    
    // Nach einem Tag erneut versuchen
    return (Date.now() - failedTimestamp) < 24 * 60 * 60 * 1000;
  } catch (error) {
    return false;
  }
};

/**
 * Entfernt eine URL aus der Liste fehlgeschlagener URLs
 */
const removeFailedUrl = (url) => {
  try {
    const failedUrls = JSON.parse(localStorage.getItem(CACHE_FAILED_URLS) || '{}');
    if (failedUrls[url]) {
      delete failedUrls[url];
      localStorage.setItem(CACHE_FAILED_URLS, JSON.stringify(failedUrls));
    }
  } catch (error) {
    console.warn("Error removing failed URL:", error);
  }
};

/**
 * Fügt eine URL zur Preload-Warteschlange hinzu
 */
export const addToPreloadQueue = (urls) => {
  if (!Array.isArray(urls) || urls.length === 0) return;
  
  const filteredUrls = urls.filter(url => {
    // Keine bereits geladenen oder fehlgeschlagenen URLs vorausladen
    return url && !memoryCache.has(url) && !hasUrlFailed(url);
  });
  
  // Zu Beginn der Queue hinzufügen für höhere Priorität
  preloadQueue = [...filteredUrls, ...preloadQueue];
  
  // Queue im LocalStorage speichern
  try {
    localStorage.setItem(CACHE_PRELOAD_QUEUE, JSON.stringify(preloadQueue));
  } catch (error) {
    console.warn("Error saving preload queue:", error);
  }
  
  // Preloading starten, wenn es nicht bereits läuft
  if (!isPreloading) {
    processPreloadQueue();
  }
};

/**
 * Verarbeitet die Preload-Queue im Hintergrund
 */
const processPreloadQueue = async () => {
  if (isPreloading || preloadQueue.length === 0) return;
  
  isPreloading = true;
  
  try {
    const batch = preloadQueue.slice(0, MAX_CONCURRENT_PRELOADS);
    preloadQueue = preloadQueue.slice(MAX_CONCURRENT_PRELOADS);
    
    // Parallel laden mit Promise.all
    await Promise.all(batch.map(url => {
      return new Promise((resolve) => {
        const img = new Image();
        
        img.onload = () => {
          // URL im Memory-Cache speichern
          memoryCache.set(url, { 
            url, 
            timestamp: Date.now() 
          });
          resolve();
        };
        
        img.onerror = () => {
          markUrlAsFailed(url);
          resolve();
        };
        
        img.src = url;
      });
    }));
    
    // Queue im LocalStorage aktualisieren
    localStorage.setItem(CACHE_PRELOAD_QUEUE, JSON.stringify(preloadQueue));
    
    // Wenn noch Items in der Queue sind, weiter verarbeiten
    if (preloadQueue.length > 0) {
      setTimeout(processPreloadQueue, 100);
    } else {
      isPreloading = false;
    }
  } catch (error) {
    console.warn("Error in preload queue processing:", error);
    isPreloading = false;
  }
};

/**
 * Höhere Priorität für bestimmte URLs festlegen
 */
const addToPreloadPriority = (url) => {
  const priorityUrls = url.split('/');
  const baseUrl = priorityUrls.slice(0, -1).join('/');
  
  // URL ähnlicher (verwandter) Bilder können priorisiert werden
  preloadQueue = preloadQueue.filter(qUrl => qUrl !== url);
};

/**
 * Löscht sämtliche Cache-Daten (für Debugging oder nach Updates)
 */
export const clearAllCaches = () => {
  // Memory-Cache leeren
  memoryCache.clear();
  preloadQueue = [];
  isPreloading = false;
  
  try {
    // Alle relevanten localStorage Einträge finden und löschen
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key.startsWith(CACHE_KEY_PREFIX) || 
          key.startsWith(CACHE_TIMING_PREFIX) || 
          key === CACHE_PRELOAD_QUEUE ||
          key === CACHE_FAILED_URLS) {
        keysToRemove.push(key);
      }
    }
    
    // Separate Schleife zum Löschen, um Iterationsprobleme zu vermeiden
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
    });
    
    return true;
  } catch (error) {
    console.error("Error clearing caches:", error);
    return false;
  }
};
